/* eslint-disable @typescript-eslint/no-explicit-any */

import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { easeQuadOut, hierarchy, select, tree, zoom, zoomIdentity } from 'd3';
import { BehaviorSubject, finalize, forkJoin, map, switchMap, catchError, of, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
  InventoryGraphEdge,
  InventoryGraphNode,
  InventoryGraphNodeResource,
  InventoryGraphNodeType,
  InventoryResourceGraph,
  ResourceStatus,
  ResourceCategory,
} from 'src/app/models/api/inventoryResource.model';
import { AiService } from 'src/app/models/api/agentStep.model';
import { InventoryHttpService } from 'src/app/services/httpServices/inventoryHttpService/inventory-http.service';
import { SidePanelService } from 'src/app/features/side-panel/services/side-panel.service';

import {
  GraphHierarchyLink,
  GraphHierarchyNode,
  InventoryGraphMerged,
  InventoryGraphMergedNodesList,
  NodeSelection,
} from './model';
import {
  addClipPath,
  countChildren,
  diameter,
  edgeRelationType,
  fullScreenBoundingBox,
  fullScreenMargin,
  imageTransform,
  isLinkInactive,
  isNodeInactive,
  GROUP_RESOURCE_TYPES,
  maxInitialZoom,
  maxZoom,
  minZoom,
  nodeIconUrl,
  nodeTransform,
  pathCurve,
  pathTextTransform,
  radius,
  singleNodeHeight,
  textTransform,
} from './utils';
import { emptySet } from 'src/app/common/functionalUtils';
import { InventoryGraphNodeCategory } from './enums';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { v4 as uuidv4 } from 'uuid';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { buildResourceTypeDisplayName } from '../../labels/utils';
import { lowerCase } from 'lodash';
import { IssuesDataService } from 'src/app/views/issues/issues.component.service';
import { ResourceType } from 'src/app/models/api/common';
import {
  InventoryIdentityResourceProperties,
  InventoryEntityFieldStatus,
} from 'src/app/models/api/inventoryResource.model';
import { Info, Users } from 'lucide-angular';

const maxGroupSize = 20;

type EnrichedInventoryGraphNode = InventoryGraphNode & {
  aiService?: AiService | null;
  totalMembersCount?: number | null;
};

@Component({
  animations: [
    trigger('fullscreen', [
      state(
        'yes',
        style({
          visibility: 'visible',
          left: `${fullScreenMargin}px`,
          top: `${fullScreenMargin}px`,
          width: `calc(100vw - 2 * ${fullScreenMargin}px)`,
          height: `calc(100vh - 2 * ${fullScreenMargin}px)`,
          opacity: 1,
        })
      ),
      state(
        'no',
        style({
          visibility: 'hidden',
          left: 'var(--left)',
          top: 'var(--top)',
          width: 'var(--width)',
          height: 'var(--height)',
          opacity: 0.5,
        })
      ),
      transition('yes => no', animate('0.2s ease-in')),
      transition('no => yes', animate('0.2s ease-out')),
    ]),
  ],
  selector: 'app-relations-graph',
  templateUrl: './relations-graph.component.html',
  styleUrls: ['./relations-graph.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RelationsGraphComponent implements OnInit {
  @ViewChild('graph', { static: true }) private readonly _graph: ElementRef;
  @ViewChild('popup') private readonly _popup: ElementRef;
  @ViewChild('tooltipFullScreen') private readonly _tooltipFullScreen: ElementRef;
  @ViewChild('tooltipSmallScreen') private readonly _tooltipSmallScreen: ElementRef;

  @HostBinding('style.--left.px') public left = 0;
  @HostBinding('style.--top.px') public top = 0;
  @HostBinding('style.--width.px') public width = 0;
  @HostBinding('style.--height.px') public height = 0;

  @Input() public integrationId: string;
  @Input() public resourceId: string;
  @Input() public isIssueComponent: boolean;
  @Input() public relatedResourcesSummary: {
    [key: string]: number;
  } = {};
  @Output() public nodeClicked = new EventEmitter<{
    integrationId: string;
    resourceId: string;
  }>();
  public isLoading$ = new BehaviorSubject(false);

  public isFullScreen = false;

  private _svgContainer: d3.Selection<SVGSVGElement, InventoryGraphNode, HTMLElement, any>;
  private _graphSvg: d3.Selection<SVGGElement, InventoryGraphNode, HTMLElement, any>;

  private _graphWidth = 0;
  private _graphHeight = 0;

  public cursorX = 0;
  public cursorY = 0;

  public mergedGraph: InventoryGraphMerged;
  public rootNode: InventoryGraphNode;
  private _virtualNodes: InventoryGraphNode[] = [];
  private readonly _inventoryGraphNodeTypeToCategory = {
    [InventoryGraphNodeType.resource]: InventoryGraphNodeCategory.resource,
    [InventoryGraphNodeType.orgWideAccess]: InventoryGraphNodeCategory.virtualNode,
    [InventoryGraphNodeType.publicAccess]: InventoryGraphNodeCategory.virtualNode,
    [InventoryGraphNodeType.m365copilot]: InventoryGraphNodeCategory.virtualNode,
    [InventoryGraphNodeType.msTeams]: InventoryGraphNodeCategory.virtualNode,
    [InventoryGraphNodeType.device]: InventoryGraphNodeCategory.virtualNode,
  };

  private readonly _sidePanelService = inject(SidePanelService);
  private readonly _clipPathId = `clip-circle-${uuidv4()}`;
  private _overflowByCategory: {
    [key: string]: { displayed: number; total: number };
  } = {};

  constructor(
    private readonly _inventoryHttpService: InventoryHttpService,
    private readonly _router: Router,
    private readonly _renderer: Renderer2,
    private readonly _beautifyDisplayString: BeautifyDisplayStringPipe,
    private readonly _issuesDataService: IssuesDataService,
    public featureFlagService: FeatureFlagsService,
    private readonly _httpClient: HttpClient
  ) {}

  private static readonly _RSA_MOCK_RESOURCE_IDS = new Set([
    'skyway-booking-chatbot-prod-001',
    'claude-code-eng-ws-001',
    'agentforce-lead-auto-handler-001',
    'rsa-identity-sarah-mitchell',
    'rsa-identity-david-chen',
    'rsa-identity-marketing-ops',
    'skyway-user-jake',
    'skyway-tool-flight-search',
    'skyway-tool-customer-db',
    'skyway-tool-send-email',
    'skyway-tool-pricing',
    'claude-user-david',
    'claude-mcp-postgres',
    'claude-db-prod-main',
    'agentforce-tool-read-leads',
    'agentforce-tool-edit-leads',
    'agentforce-tool-send-email',
    'agentforce-crm-lead-object',
  ]);

  private static readonly _IAM_DEMO_MOCK_RESOURCE_IDS = new Set([
    '02a1b3cd4ef56789abcde1234567890a',
    '13b7c4e2d09f1a3e8b5c6d7e4f2a1b0c',
    '24c8d5f3e10g2b4f9c6d7e5f3b2c1d0e',
    'crm-insight-agent-resource-id',
    '35d9e6g4f21h3c5g0d7e8f6g4c3d2e1f',
    '4822e539291f4bc96758c5f37bbc693f',
    '5933f640302g5cd07869d6g48ccd704g',
    'chris-smith-identity-id',
    'spaiware-chatgpt-enterprise-001',
    'spaiware-chatgpt-app-001',
    'spaiware-attachment-eoy-001',
    'spaiware-memory-001',
    'spaiware-ethan-identity-001',
    'cursor-mcp-david-chen-identity',
    'cursor-mcp-cursor-agent-session',
    'cursor-mcp-postgres-mcp-server',
    'cursor-mcp-github-mcp-server',
    'cursor-mcp-filesystem-mcp-server',
    'cursor-mcp-production-db',
    'cursor-mcp-github-repo',
    'cursor-mcp-db-service-token',
    'cursor-mcp-github-oauth-token',
    'cursor-mcp-skill-code-debug',
  ]);

  public ngOnInit(): void {
    this.isLoading$.next(true);
    this.mergedGraph = {
      mergedGraphDictionary: {},
    };

    const isRsaMock =
      this.featureFlagService.isFeatureEnabled(
        FeatureFlagTypes.RsaDemoRuntimeBoundaries
      ) &&
      RelationsGraphComponent._RSA_MOCK_RESOURCE_IDS.has(
        this.resourceId
      );
    const isIamDemoMock =
      this.featureFlagService.isFeatureEnabled(
        FeatureFlagTypes.SpmIamDemoAmex
      ) &&
      RelationsGraphComponent._IAM_DEMO_MOCK_RESOURCE_IDS.has(
        this.resourceId
      );

    if (isRsaMock || isIamDemoMock) {
      const jsonPath = isIamDemoMock
        ? 'assets/mockData/iamDemoGraphMockData.json'
        : 'assets/mockData/rsaGraphMockData.json';
      this._httpClient
        .get<Record<string, InventoryResourceGraph>>(jsonPath)
        .pipe(
          map((data) => data[this.resourceId]),
          finalize(() => this.isLoading$.next(false))
        )
        .subscribe((graph) => {
          if (graph) {
            requestAnimationFrame(() => this._onGraphLoaded(graph));
          }
        });
    } else {
      this._inventoryHttpService
        .getInventoryResourceGraph(this.integrationId, this.resourceId, 2)
        .pipe(
          switchMap((graph) => this._enrichGraphDataWithAiService(graph)),
          finalize(() => this.isLoading$.next(false))
        )
        .subscribe((enrichedGraph) => this._onGraphLoaded(enrichedGraph));
    }

    this._updatePositionBindings(this._boundingBox());
  }
  @HostListener('document:mousemove', ['$event'])
  public onMouseMove(e: MouseEvent) {
    if (this.isFullScreen) {
      this.cursorX = e.clientX + 10 - fullScreenMargin;
      this.cursorY = e.clientY + 10 - fullScreenMargin;
      this._renderer.setStyle(this._tooltipFullScreen.nativeElement, 'left', this.cursorX.toString() + 'px');
      this._renderer.setStyle(this._tooltipFullScreen.nativeElement, 'top', this.cursorY.toString() + 'px');
    } else {
      this.cursorX = e.clientX + 10 - this._boundingBox().x;
      this.cursorY = e.clientY + 10 - this._boundingBox().y;
      if (this.cursorX > this._boundingBox().width - 150) {
        this.cursorX -= 150;
        this.cursorY += 10;
      }

      if (this.cursorY > this._boundingBox().height - 50) {
        this.cursorY -= 50;
      }
      this._renderer.setStyle(this._tooltipSmallScreen.nativeElement, 'left', this.cursorX.toString() + 'px');
      this._renderer.setStyle(this._tooltipSmallScreen.nativeElement, 'top', this.cursorY.toString() + 'px');
    }
  }
  private readonly _boundingBox = () => this._graph.nativeElement.getBoundingClientRect();

  private _createVirtualNodes(nodes: InventoryGraphNode[]): void {
    const virtualNodes = nodes.filter((node) => node.resource.id.startsWith('virtual_node'));

    virtualNodes.forEach((node) => {
      this._virtualNodes.push(node);
      node.iconUrl = this._getVirtualNodeIconUrl(node.nodeType);
      node.parentRelationType = this._getVirtualNodeRelationType(node);
    });
  }

  private _getVirtualNodeIconUrl(nodeType: InventoryGraphNodeType): string {
    const iconUrlMap: Partial<Record<InventoryGraphNodeType, string>> = {
      [InventoryGraphNodeType.publicAccess]: 'assets/images/common/globe-colored.svg',
      [InventoryGraphNodeType.orgWideAccess]: 'assets/images/common/organization-colored.svg',
      [InventoryGraphNodeType.m365copilot]: 'assets/images/logo/m365Copilot.svg',
      [InventoryGraphNodeType.device]: 'assets/images/resource-category-icons/devices-colored.svg',
      [InventoryGraphNodeType.msTeams]: 'assets/images/logo/agent-client-app-teams.svg',
    };

    return iconUrlMap[nodeType] || '';
  }

  private _getVirtualNodeRelationType(node: InventoryGraphNode): string {
    const firstEdge = node.edges?.[0];
    if (!firstEdge) {
      console.warn(`Virtual node ${node.resource.id} has no edges for relation type`);
      return '';
    }
    return edgeRelationType(firstEdge);
  }

  private _copyEnrichedDataFromSourceToTargetNode(
    sourceNode: InventoryGraphNode,
    targetNode: InventoryGraphNode
  ): void {
    const enrichedSource = sourceNode as EnrichedInventoryGraphNode;
    const enrichedTarget = targetNode as EnrichedInventoryGraphNode;
    if (enrichedSource.aiService) {
      enrichedTarget.aiService = enrichedSource.aiService;
    }
    if (typeof enrichedSource.totalMembersCount === 'number') {
      enrichedTarget.totalMembersCount = enrichedSource.totalMembersCount;
    }
  }

  private _calculateGraphDimensions(
    leaves: d3.HierarchyNode<InventoryGraphNode>[],
    virtualNodesCount: number,
    maxDepth: number
  ): void {
    const regularChildrenCount = leaves?.length ?? 0;
    const totalNodesHeight = Math.max(regularChildrenCount * singleNodeHeight, virtualNodesCount * singleNodeHeight);
    this._graphHeight = totalNodesHeight;
    this._graphWidth = maxDepth * 300;
  }

  private _createVirtualNodesForRendering(
    root: GraphHierarchyNode,
    hasVirtualNodes: boolean,
    virtualNodesCount: number
  ): GraphHierarchyNode[] {
    if (!hasVirtualNodes) {
      return [];
    }

    const rootX = root.x;
    const rootY = root.y;
    const virtualNodeSpacing = singleNodeHeight;
    const totalVirtualHeight = (virtualNodesCount - 1) * virtualNodeSpacing;
    const startX = rootX - totalVirtualHeight / 2;

    return this._virtualNodes.map(
      (virtualNode, index) =>
        ({
          data: virtualNode,
          x: startX + index * virtualNodeSpacing,
          y: rootY - 300,
          depth: 1,
          height: 0,
          parent: root,
          children: null,
          ancestors: () => [root],
          descendants: () => [],
          leaves: () => [],
          find: () => null,
          sum: () => 0,
          sort: () => null,
          path: () => [],
          copy: () => null,
          count: () => 1,
          each: () => {},
          eachAfter: () => {},
          eachBefore: () => {},
        }) as unknown as GraphHierarchyNode
    );
  }

  private _renderLinks(root: GraphHierarchyNode, virtualNodesForRendering: GraphHierarchyNode[]): void {
    const allLinks = [
      ...root.links(),
      ...virtualNodesForRendering.map(
        (virtualNode) =>
          ({
            source: root,
            target: virtualNode,
          }) as GraphHierarchyLink
      ),
    ];

    const link = this._graphSvg
      .selectAll<SVGPathElement, GraphHierarchyLink>('.link')
      .data(allLinks)
      .enter()
      .append('g')
      .classed('link-group', true);

    link
      .append('path')
      .attr('d', pathCurve as never)
      .classed('link', true)
      .classed('disabled-link', isLinkInactive as never);

    link
      .append('text')
      .html((linkData) =>
        this._changeDisplayNameToBeMultipleLinesIfTooLong(linkData.target.data.parentRelationType || '')
      )
      .attr('text-anchor', 'middle')
      .attr('transform', pathTextTransform as never)
      .on('mouseover', (_, linkData) => this._handleNodeMouseOverTooltip(linkData.target.data.parentRelationType))
      .on('mouseout', () => this._handleNodeMouseOutTooltip())
      .classed('link-text', true);
  }

  private _renderNodes(root: GraphHierarchyNode, virtualNodesForRendering: GraphHierarchyNode[]): void {
    const allNodes = [...root.descendants(), ...virtualNodesForRendering];

    const nodes = this._graphSvg
      .selectAll<SVGGElement, GraphHierarchyNode>('.node')
      .data(allNodes)
      .enter()
      .append('g')
      .attr('transform', nodeTransform)
      .classed('node', true);

    this._renderNodeCircles(nodes);
    this._renderNodeImages(nodes);
    this._renderNodeText(nodes);
    this._renderMembersBadge(nodes);
    this._renderOverflowBadge(nodes);
  }

  private _renderNodeCircles(nodes: NodeSelection): void {
    nodes
      .append('circle')
      .classed('root-node', this._isRootNode)
      .classed('highlight', true)
      .classed(
        'virtual-node',
        (node) =>
          node.data.resource.id.startsWith('virtual_node') ||
          this._virtualNodes.some((vn) => vn.resource.id === node.data.resource.id)
      )
      .classed('ai-service-node', this._isAiServiceNode);
  }

  private _renderNodeImages(nodes: NodeSelection): void {
    nodes
      .append('image')
      .attr('href', nodeIconUrl)
      .attr('width', diameter)
      .attr('height', diameter)
      .attr('cursor', (node) => (node.data.nonClickable ? 'default' : 'pointer'))
      .attr('transform', imageTransform)
      .attr('clip-path', `url(#${this._clipPathId})`)
      .classed('inactive-node', isNodeInactive)
      .classed('node-image', true)
      .on('click', (event, node) => this._onNodeClicked(event, node))
      .on('mouseover', (_, node) => this._handleNodeMouseOver(nodes, node, true))
      .on('mouseout', (_, node) => this._handleNodeMouseOver(nodes, node, false));
  }

  private _renderNodeText(nodes: NodeSelection): void {
    nodes
      .append('text')
      .html((node) =>
        this._addResourceTypeToDisplayNameIfNeeded(
          this._changeDisplayNameToBeMultipleLinesIfTooLong(node.data.displayName),
          node.data
        )
      )
      .attr('text-anchor', 'middle')
      .attr('transform', textTransform)
      .on('mouseover', (_, node) => this._handleNodeMouseOverTooltip(node.data.displayName))
      .on('mouseout', () => this._handleNodeMouseOutTooltip());
  }

  private _renderMembersBadge(nodes: NodeSelection): void {
    const groupNodes = nodes.filter((node) => this._isGroupNode(node) && this._hasMembersCountData(node));
    const badgeWidth = 40;
    const badgeHeight = 20;
    const xOffset = 0;
    const yOffset = -radius - 8;

    const usersIconSvg = this._lucideIconToSvg(Users, 'members-icon');

    groupNodes
      .append('foreignObject')
      .attr('width', badgeWidth)
      .attr('height', badgeHeight)
      .attr('x', xOffset)
      .attr('y', yOffset)
      .append('xhtml:div')
      .attr('class', 'members-badge-container')
      .on('mouseover', () => this._handleNodeMouseOverTooltip('Unique user count in group'))
      .on('mouseout', () => this._handleNodeMouseOutTooltip())
      .html((node) => {
        const membersCount = this._getMembersCount(node);
        return `
          <span class="members-badge">
            ${usersIconSvg}
            <span class="members-count">${membersCount}</span>
          </span>
        `;
      });
  }

  private _getTruncatedDisplayName(originalDisplayName: string): string {
    const maxLength = 24;
    if (originalDisplayName.length < maxLength) {
      return originalDisplayName;
    }
    for (let i = maxLength; i > 0; i--) {
      if (originalDisplayName[i] === ' ') {
        return originalDisplayName.slice(0, i) + '...';
      }
    }
    return originalDisplayName.slice(0, maxLength - 3) + '...';
  }

  private _isGroupNode(node: GraphHierarchyNode): boolean {
    return GROUP_RESOURCE_TYPES.includes(node.data.resource.type as ResourceType);
  }

  private _hasMembersCountData(node: GraphHierarchyNode): boolean {
    const enrichedData = node.data as EnrichedInventoryGraphNode;
    return typeof enrichedData.totalMembersCount === 'number';
  }

  private _getMembersCount(node: GraphHierarchyNode): number {
    const enrichedData = node.data as EnrichedInventoryGraphNode;
    return enrichedData.totalMembersCount ?? 0;
  }

  private _lucideIconToSvg(icon: typeof Users, className: string): string {
    const svgContent = icon
      .map((node) => {
        const [tag, attrs] = node;
        const attrStr = Object.entries(attrs)
          .map(([key, value]) => `${key}="${value}"`)
          .join(' ');
        return `<${tag} ${attrStr}/>`;
      })
      .join('');

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="${className}">${svgContent}</svg>`;
  }

  private _countDisplayedByCategory(graphNodes: InventoryGraphNode[]): void {
    const displayed: { [key: string]: number } = {};
    graphNodes.forEach((node) => {
      if (node.resource.id === this.resourceId) return;
      if (node.resource.id.startsWith('virtual_node')) return;
      const cat = node.resource.category;
      displayed[cat] = (displayed[cat] ?? 0) + 1;
    });

    this._overflowByCategory = {};
    for (const cat in this.relatedResourcesSummary) {
      const total = this.relatedResourcesSummary[cat] ?? 0;
      const disp = displayed[cat] ?? 0;
      if (total > disp) {
        this._overflowByCategory[cat] = { displayed: disp, total };
      }
    }
  }

  private _renderOverflowBadge(nodes: NodeSelection): void {
    const overflowNodes = nodes.filter(
      (node) =>
        node.data.resource.id.startsWith('merged') &&
        (node.data.mergedGroupSize ?? 0) >= maxGroupSize &&
        this._overflowByCategory[node.data.resource.category] != null
    );

    if (overflowNodes.empty()) return;

    const badgeWidth = 40;
    const badgeHeight = 20;
    const xOffset = radius - 4;
    const yOffset = -radius - badgeHeight + 4;
    const infoIconSvg = this._lucideIconToSvg(Info, 'overflow-icon');

    overflowNodes
      .append('foreignObject')
      .attr('width', badgeWidth)
      .attr('height', badgeHeight)
      .attr('x', xOffset)
      .attr('y', yOffset)
      .append('xhtml:div')
      .attr('class', 'overflow-badge-container')
      .on('mouseover', (_event: MouseEvent, node: GraphHierarchyNode) => {
        const cat = node.data.resource.category;
        const overflow = this._overflowByCategory[cat];
        if (!overflow) return;
        const catName = this._beautifyDisplayString.transform(cat);
        const text =
          `Showing ${node.data.mergedGroupSize ?? 0}` +
          ` of ${overflow.total} ${catName} resources.` +
          ` Click to view all in Inventory`;
        this._handleNodeMouseOverTooltip(text);
      })
      .on('mouseout', () => this._handleNodeMouseOutTooltip())
      .on('click', (_event: MouseEvent, node: GraphHierarchyNode) => {
        _event.stopPropagation();
        this._navigateToFilteredInventory(node.data.resource.category);
      })
      .html(() => {
        return `
          <span class="overflow-badge">
            ${infoIconSvg}
          </span>
        `;
      });
  }

  private _onGraphLoaded(graph: InventoryResourceGraph) {
    this._countDisplayedByCategory(graph.graphNodes);
    const nodeMap = graph.graphNodes.toMapBy((node) => node.resource.id);
    const distinctNodesMap: { [distinctId: string]: InventoryGraphNode } = {};
    const visited = emptySet<string>();

    const buildChildren = (node: InventoryGraphNode, mergedGraph: InventoryGraphMerged): InventoryGraphNode[] => {
      visited.add(node.resource.id);

      // Skip recursive processing for virtual nodes
      if (node.resource.id.startsWith('virtual_node')) {
        return node.children ?? [];
      }

      const edgeNode = (edge: InventoryGraphEdge) => {
        const originalNode = nodeMap.get(edge.targetResourceId);
        const newNode = {} as InventoryGraphNode;
        Object.assign(newNode, originalNode);
        newNode.distinctId = uuidv4(); // When we get the nodes they don't have distinct ids, so we need to add them.

        if (originalNode) {
          this._copyEnrichedDataFromSourceToTargetNode(originalNode, newNode);
        }

        distinctNodesMap[newNode.distinctId] = newNode; // We need to save the distinct nodes in a map so we can access them later
        return newNode;
      };
      node.distinctChildrenIds = [];
      node?.edges?.forEach((edge) => {
        edgeNode(edge)?.let((nodeInside) => {
          nodeInside.parentRelationType = edgeRelationType(edge);
          if (nodeInside.distinctId) node.distinctChildrenIds?.push(nodeInside.distinctId);
        });
      });

      // Recursively build children (exclude virtual nodes — they are rendered separately)
      node.children = node?.distinctChildrenIds
        ?.map((distinctId) => distinctNodesMap[distinctId]!)
        ?.filter((child) => !visited.has(child.resource.id) && !child.resource.id.startsWith('virtual_node'))
        ?.alsoForEach((child) => buildChildren(child, mergedGraph));

      // This part is adding the nodes to the merged graph (parent -> relationType -> resourceType -> hasChild -> {areMerged: boolean, nodes: InventoryGraphNode[]})
      mergedGraph.mergedGraphDictionary[node.resource.id] = {};
      node.children?.forEach((child) => {
        if (mergedGraph.mergedGraphDictionary[node.resource.id][child.parentRelationType] == null) {
          mergedGraph.mergedGraphDictionary[node.resource.id][child.parentRelationType] = {};
        }
        if (
          mergedGraph.mergedGraphDictionary[node.resource.id][child.parentRelationType][child.resource.type] == null
        ) {
          mergedGraph.mergedGraphDictionary[node.resource.id][child.parentRelationType][child.resource.type] = {};
        }
        let hasChild = 'false';
        if (child.children && child.children.length > 0) hasChild = 'true';
        if (
          mergedGraph.mergedGraphDictionary[node.resource.id][child.parentRelationType][child.resource.type][
            hasChild
          ] == null
        ) {
          if (hasChild === 'true') {
            mergedGraph.mergedGraphDictionary[node.resource.id][child.parentRelationType][child.resource.type][
              hasChild
            ] = {
              areMerged: false,
              nodes: [],
            };
          } else {
            mergedGraph.mergedGraphDictionary[node.resource.id][child.parentRelationType][child.resource.type][
              hasChild
            ] = {
              areMerged: true,
              nodes: [],
            };
          }
        }
        mergedGraph.mergedGraphDictionary[node.resource.id][child.parentRelationType][child.resource.type][
          hasChild
        ].nodes.push(child);
      });
      // End of adding nodes to the merged graph

      return node.children ?? [];
    };

    // We want to find the first node in the graph - in can be the root node or the access node
    let root = graph.graphNodes.find((node) => node.resource.id === this.resourceId);

    if (root == null) return;

    // Create virtual nodes manually since they don't exist in the data
    this._createVirtualNodes(graph.graphNodes);

    root.children = buildChildren(root, this.mergedGraph); // Start building the graph from the first node recursively
    this.rootNode = root;
    this._buildGraph();
  }

  private _buildNewRootNode(rootNode: InventoryGraphNode) {
    const newRoot = {
      _links: rootNode._links,
      resource: rootNode.resource,
      displayName: rootNode.displayName,
      iconUrl: rootNode.iconUrl,
      edges: [],
      children: [],
      parentRelationType: rootNode.parentRelationType,
      nodeType: rootNode.nodeType,
      nonClickable: rootNode.nonClickable,
    } as InventoryGraphNode;

    this._copyEnrichedDataFromSourceToTargetNode(rootNode, newRoot);

    // Virtual nodes will be added separately after tree layout

    if (this.mergedGraph.mergedGraphDictionary[rootNode.resource.id] == null) {
      return newRoot;
    }

    Object.keys(this.mergedGraph.mergedGraphDictionary[rootNode.resource.id]).forEach((parentRelationType) => {
      Object.keys(this.mergedGraph.mergedGraphDictionary[rootNode.resource.id][parentRelationType]).forEach(
        (resourceType) => {
          Object.keys(
            this.mergedGraph.mergedGraphDictionary[rootNode.resource.id][parentRelationType][resourceType]
          ).forEach((hasChild) => {
            const nodes: InventoryGraphMergedNodesList =
              this.mergedGraph.mergedGraphDictionary[rootNode.resource.id][parentRelationType][resourceType][hasChild];

            if (hasChild === 'true') {
              nodes.nodes.forEach((node: InventoryGraphNode) => {
                newRoot.children?.push(this._buildNewRootNode(node));
              });
            } else {
              if (!nodes.areMerged || nodes.nodes.length === 1) {
                nodes.nodes.forEach((node: InventoryGraphNode) => {
                  newRoot.children?.push(this._buildNewRootNode(node));
                });
              } else {
                const groupSize = nodes.nodes.length;
                const cat = nodes.nodes[0].resource.category;
                const hasOverflow = groupSize >= maxGroupSize && this._overflowByCategory[cat] != null;
                const countLabel = hasOverflow ? `${groupSize}+` : `${groupSize}`;
                const newNode = {
                  _links: rootNode._links,
                  resource: {
                    id: 'merged-' + nodes.nodes.map((node) => node.resource.id).join('_'),
                    status: ResourceStatus.active,
                    category: cat,
                    type: nodes.nodes[0].resource.type,
                  } as InventoryGraphNodeResource,
                  displayName:
                    'More ' +
                    lowerCase(buildResourceTypeDisplayName(nodes.nodes[0].resource.type)) +
                    's (' +
                    countLabel +
                    ')',
                  mergedGroupSize: groupSize,
                  iconUrl: nodes.nodes[0].iconUrl,
                  edges: [],
                  children: [],
                  parentRelationType: nodes.nodes[0].parentRelationType,
                  nodeType: InventoryGraphNodeType.resource,
                } as InventoryGraphNode;
                newRoot.children?.push(newNode);
              }
            }
          });
        }
      );
    });

    return newRoot;
  }

  private _buildGraph() {
    this._svgContainer = select<SVGGElement, InventoryGraphNode>(this._graph.nativeElement)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('id', 'graph-svg')
      .also((svg) => addClipPath(svg, this._clipPathId));

    this._graphSvg = this._svgContainer.append('g');

    const newRoot = this._buildNewRootNode(this.rootNode);

    // Don't integrate virtual nodes into the hierarchy - they will be positioned manually
    // This prevents D3 from treating them as regular children and affecting the right side layout
    const virtualNodesCount = this._virtualNodes.length;
    const hasVirtualNodes = virtualNodesCount > 0;

    const root = hierarchy<InventoryGraphNode>(newRoot) as GraphHierarchyNode;
    const leaves = root.leaves();
    const maxDepth = leaves?.reduce((maxDepth, leaf) => (maxDepth = Math.max(leaf.depth, maxDepth)), 0) ?? 0;

    this._calculateGraphDimensions(leaves, virtualNodesCount, maxDepth);

    // Apply tree layout to the complete hierarchy (including virtual nodes)
    // Use the original separation logic for all nodes to maintain original spacing behavior
    tree<InventoryGraphNode>()
      .separation((a, b) => countChildren(a) + countChildren(b))
      .size([this._graphHeight, this._graphWidth])(root);

    const virtualNodesForRendering = this._createVirtualNodesForRendering(root, hasVirtualNodes, virtualNodesCount);

    this._renderLinks(root, virtualNodesForRendering);

    this._renderNodes(root, virtualNodesForRendering);
    this._resetZoom();
  }
  private _handleNodeMouseOverTooltip(text: string): void {
    if (this.isFullScreen) {
      this._renderer.setStyle(this._tooltipFullScreen.nativeElement, 'display', 'block');
      this._renderer.setProperty(this._tooltipFullScreen.nativeElement, 'innerHTML', text);
    } else {
      if (this.cursorX > this._boundingBox().width - 32 * 2 && this.cursorY > this._boundingBox().height - 32 * 2) {
        return;
      }
      this._renderer.setStyle(this._tooltipSmallScreen.nativeElement, 'display', 'block');
      this._renderer.setProperty(this._tooltipSmallScreen.nativeElement, 'innerHTML', text);
    }
  }
  private _handleNodeMouseOutTooltip(): void {
    if (this.isFullScreen) {
      this._renderer.setProperty(this._tooltipFullScreen.nativeElement, 'innerHTML', '');
      this._renderer.setStyle(this._tooltipFullScreen.nativeElement, 'display', 'none');
    } else {
      this._renderer.setProperty(this._tooltipSmallScreen.nativeElement, 'innerHTML', '');
      this._renderer.setStyle(this._tooltipSmallScreen.nativeElement, 'display', 'none');
    }
  }

  private _addResourceTypeToDisplayNameIfNeeded(displayName: string, node: InventoryGraphNode) {
    if (node.nodeType !== InventoryGraphNodeType.resource) return displayName;
    return (
      displayName +
      `<tspan x="0" dy="15" style="font-size: 10px; fill:rgba(105, 104, 121, 1)">${buildResourceTypeDisplayName(node.resource.type)}</tspan>`
    );
  }

  private _changeDisplayNameToBeMultipleLinesIfTooLong(originalDisplayName: string) {
    const originalDisplayNameLength = originalDisplayName.length;
    const sizeOfDisplayNameMax = 24;
    let firstLine = originalDisplayName;

    if (originalDisplayNameLength >= sizeOfDisplayNameMax) {
      for (let i = sizeOfDisplayNameMax; i > 0; i--) {
        if (originalDisplayName[i] === ' ') {
          firstLine = originalDisplayName.slice(0, i);
          break;
        }
      }
      if (firstLine.length > sizeOfDisplayNameMax) {
        firstLine = firstLine.slice(0, sizeOfDisplayNameMax - 3) + '...';
      } else {
        firstLine = firstLine + '...';
      }
    }
    return `<tspan x="0" dy="0">${firstLine}</tspan>`;
  }

  private _resetZoom() {
    const parentElement = this._svgContainer?.node()?.parentElement;
    if (parentElement == null) return;

    const fullScreen = parentElement === this._popup.nativeElement;
    // For full screen, we want to take the final size post animation
    const containerBoundingBox = fullScreen ? fullScreenBoundingBox() : parentElement.getBoundingClientRect();

    const svgBBox = this._graphSvg.node()?.getBBox();
    const contentWidth = svgBBox ? svgBBox.width : this._graphWidth;
    const contentHeight = svgBBox ? svgBBox.height : this._graphHeight;

    const scaleX = containerBoundingBox.width / contentWidth;
    const scaleY = containerBoundingBox.height / contentHeight;
    const scale = Math.max(minZoom, Math.min(scaleX, scaleY, maxInitialZoom));
    const translateX = (containerBoundingBox.width - contentWidth * scale) / 2 - (svgBBox?.x ?? 0) * scale;
    const translateY = (containerBoundingBox.height - contentHeight * scale) / 2 - (svgBBox?.y ?? 0) * scale;
    const graphZoom = zoom<SVGSVGElement, InventoryGraphNode>().extent([
      [0, 0],
      [300, 300],
    ]);

    this._graphSvg
      .attr('transform', `translate(${translateX} ${translateY}) scale(${scale})`)
      .attr('opacity', 0)
      .transition()
      .ease(easeQuadOut)
      .duration(500)
      .attr('opacity', 1)
      .on('end', () =>
        this._svgContainer
          .call(
            graphZoom
              .scaleExtent([minZoom, maxZoom])
              .on('zoom', (event) => this._graphSvg.attr('transform', event.transform))
          )
          .call(graphZoom.transform, zoomIdentity.translate(translateX, translateY).scale(scale))
      );
  }

  private _handleNodeMouseOver(nodes: NodeSelection, currentNode: GraphHierarchyNode, enable: boolean) {
    if (currentNode.data.resource.id === this.resourceId)
      nodes
        .select('circle')
        .filter(
          (node) => node.data.resource.id === currentNode.data.resource.id && node.data.resource.id === this.resourceId
        )
        .classed('root-node', !enable);

    const targetCircles = nodes
      .select('circle')
      .filter((node) => node.data.resource.id === currentNode.data.resource.id);

    // Add hover class for AI Service nodes to show border on hover
    targetCircles
      .classed('node-hover', enable && this._isAiServiceNode(currentNode))
      .transition()
      .duration(180)
      .attr('r', radius + (enable ? 3 : -3))
      .attr('opacity', enable ? 1 : 0);
  }

  private _onNodeClicked(event: PointerEvent, node: GraphHierarchyNode) {
    event.stopPropagation();

    // Handle merged node expansion for all contexts (including issue component)
    if (node.data.resource.id.startsWith('merged')) {
      if (
        node.parent &&
        this.mergedGraph.mergedGraphDictionary[node.parent?.data.resource.id]?.[node.data.parentRelationType]?.[
          node.data.resource.type
        ]?.['false']?.areMerged
      ) {
        this.mergedGraph.mergedGraphDictionary[node.parent?.data.resource.id][node.data.parentRelationType][
          node.data.resource.type
        ]['false'].areMerged = false;
        this._rebuildGraph();
        return;
      }
    }

    if (this.isIssueComponent) {
      // Emit node click event for issue component to handle
      // Skip virtual nodes, merged nodes, and non-clickable nodes
      if (
        !node.data.nonClickable &&
        !node.data.resource.id.startsWith('virtual_node') &&
        !node.data.resource.id.startsWith('merged') &&
        this._inventoryGraphNodeTypeToCategory[node.data.nodeType] !== InventoryGraphNodeCategory.virtualNode
      ) {
        this.nodeClicked.emit({
          integrationId: this.integrationId,
          resourceId: node.data.resource.id,
        });
      }
      return;
    }

    if (this._inventoryGraphNodeTypeToCategory[node.data.nodeType] === InventoryGraphNodeCategory.virtualNode) {
      if (node.data.nodeType === InventoryGraphNodeType.publicAccess)
        this._router.navigate(['app/inventory'], {
          queryParams: {
            q: 'publicAccess:true',
          },
        });
      else if (node.data.nodeType === InventoryGraphNodeType.orgWideAccess)
        this._router.navigate(['app/inventory'], {
          queryParams: {
            q: 'orgWideAccess:true',
          },
        });
      return;
    }

    // Auto-stacking: will stack if panel is open, otherwise opens as main
    this._sidePanelService.navigateStacked({
      resource: node.data.resource.id,
      integration: this.integrationId,
    });
  }

  private _navigateToFilteredInventory(category: string): void {
    this._router.navigate(['app/inventory'], {
      queryParams: {
        q: `relatedResource:${this.resourceId}` + ` integration:${this.integrationId}` + ` category:${category}`,
      },
    });
  }

  private _rebuildGraph() {
    const startIsFullScreen = this.isFullScreen;
    if (this.isFullScreen) {
      this.setFullScreen();
    }
    const svgGraph = document.getElementById('graph-svg');
    svgGraph?.remove();
    this._buildGraph();
    if (startIsFullScreen) {
      this.setFullScreen(true);
    }
  }

  public expandAllNodes() {
    Object.keys(this.mergedGraph.mergedGraphDictionary).forEach((nodeId) => {
      Object.keys(this.mergedGraph.mergedGraphDictionary[nodeId]).forEach((parentRelationType) => {
        Object.keys(this.mergedGraph.mergedGraphDictionary[nodeId][parentRelationType]).forEach((resourceType) => {
          Object.keys(this.mergedGraph.mergedGraphDictionary[nodeId][parentRelationType][resourceType]).forEach(
            (hasChild) => {
              const nodes = this.mergedGraph.mergedGraphDictionary[nodeId][parentRelationType][resourceType][hasChild];
              if (hasChild === 'false') {
                nodes.areMerged = false;
              }
            }
          );
        });
      });
    });

    this._rebuildGraph();
  }

  public collapseAllNodes() {
    Object.keys(this.mergedGraph.mergedGraphDictionary).forEach((nodeId) => {
      Object.keys(this.mergedGraph.mergedGraphDictionary[nodeId]).forEach((parentRelationType) => {
        Object.keys(this.mergedGraph.mergedGraphDictionary[nodeId][parentRelationType]).forEach((resourceType) => {
          Object.keys(this.mergedGraph.mergedGraphDictionary[nodeId][parentRelationType][resourceType]).forEach(
            (hasChild) => {
              const nodes = this.mergedGraph.mergedGraphDictionary[nodeId][parentRelationType][resourceType][hasChild];
              if (hasChild === 'false') {
                nodes.areMerged = true;
              }
            }
          );
        });
      });
    });

    this._rebuildGraph();
  }

  public setFullScreen(fullScreen = !this.isFullScreen) {
    if (this.isFullScreen === fullScreen) return;

    this.isFullScreen = fullScreen;

    this._updatePositionBindings(this._boundingBox());

    const [source, target] = this.isFullScreen ? [this._graph, this._popup] : [this._popup, this._graph];
    this._reparentGraph(source, target);

    this._resetZoom();
  }

  private _reparentGraph(source: ElementRef, target: ElementRef) {
    const svgElement = source.nativeElement.querySelector('svg');
    this._renderer.removeChild(source.nativeElement, svgElement);
    this._renderer.appendChild(target.nativeElement, svgElement);
  }

  private _updatePositionBindings(rect: DOMRect) {
    this.left = rect.x;
    this.top = rect.y;
    this.width = rect.width;
    this.height = rect.height;
  }

  @HostListener('window:keydown', ['$event'])
  public onKeyEvent(event: KeyboardEvent) {
    switch (event.key) {
      // case 'Enter':
      //     event.stopImmediatePropagation()
      //     this.setFullScreen(true)
      //     break

      case 'Escape':
        event.stopImmediatePropagation();
        this.setFullScreen(false);
        break;
    }
  }

  private _enrichGraphDataWithAiService(graph: InventoryResourceGraph) {
    const botNodes = graph.graphNodes.filter((node) => node.resource.category === ResourceCategory.bot);
    const groupNodes = graph.graphNodes.filter((node) =>
      GROUP_RESOURCE_TYPES.includes(node.resource.type as ResourceType)
    );

    if (botNodes.length === 0 && groupNodes.length === 0) {
      return of(graph);
    }

    const botEnrichRequests = botNodes.map((node) =>
      this._inventoryHttpService.getInventoryResource(this.integrationId, node.resource.id).pipe(
        map((fullResource) => ({
          node,
          aiService: fullResource.aiService,
          totalMembersCount: null as number | null,
        })),
        catchError(() => {
          return of({ node, aiService: null, totalMembersCount: null as number | null });
        })
      )
    );

    const groupEnrichRequests = groupNodes.map((node) =>
      this._inventoryHttpService.getInventoryResource(this.integrationId, node.resource.id).pipe(
        map((fullResource) => {
          const identityProps = fullResource.resourceCategoryProperties as
            | InventoryIdentityResourceProperties
            | undefined;
          const hasValue = identityProps?.totalMembersCount?.status === InventoryEntityFieldStatus.hasValue;
          const membersCount = hasValue ? (identityProps?.totalMembersCount?.value ?? null) : null;
          return {
            node,
            aiService: null as AiService | undefined | null,
            totalMembersCount: membersCount,
          };
        }),
        catchError(() => {
          return of({
            node,
            aiService: null as AiService | undefined | null,
            totalMembersCount: null as number | null,
          });
        })
      )
    );

    const allEnrichRequests = [...botEnrichRequests, ...groupEnrichRequests];

    return forkJoin(allEnrichRequests).pipe(
      map((results) => {
        results.forEach(({ node, aiService, totalMembersCount }) => {
          const enrichedNode = node as EnrichedInventoryGraphNode;
          if (aiService) {
            enrichedNode.aiService = aiService;
          }
          if (typeof totalMembersCount === 'number') {
            enrichedNode.totalMembersCount = totalMembersCount;
          }
        });
        return graph;
      })
    );
  }

  private readonly _isAiServiceNode = (node: GraphHierarchyNode): boolean => {
    const enrichedData = node.data as EnrichedInventoryGraphNode;
    return node.data.resource.category === ResourceCategory.bot && !!enrichedData.aiService;
  };

  private readonly _isRootNode = (node: GraphHierarchyNode) => node.data.resource.id === this.resourceId;
}
